import { apiClient } from './client';

const STORAGE_KEY = 'website-analytics-session';
const VISITOR_KEY = 'website-analytics-visitor-v1';
const VISITOR_FIRST_SEEN_KEY = 'website-analytics-first-seen-v1';
/** First-party anonymous browser identity. Clear site data to reset; respect opt-out. */
function visitorIdentity(): { visitorId: string; visitorFirstSeen: string } | null {
  try {
    if (localStorage.getItem('website-analytics-opt-out') === '1') return null;
    let visitorId = localStorage.getItem(VISITOR_KEY);
    if (!visitorId || !/^[a-f0-9]{32}$/.test(visitorId)) {
      visitorId = crypto.randomUUID().replace(/-/g, '');
      localStorage.setItem(VISITOR_KEY, visitorId);
      localStorage.setItem(VISITOR_FIRST_SEEN_KEY, new Date().toISOString().slice(0, 10));
    }
    let visitorFirstSeen = localStorage.getItem(VISITOR_FIRST_SEEN_KEY);
    if (!visitorFirstSeen || !/^\d{4}-\d{2}-\d{2}$/.test(visitorFirstSeen)) {
      visitorFirstSeen = new Date().toISOString().slice(0, 10);
      localStorage.setItem(VISITOR_FIRST_SEEN_KEY, visitorFirstSeen);
    }
    return { visitorId, visitorFirstSeen };
  } catch { return null; } // No persistent identifier if browser storage is unavailable.
}

let lastPageView = '';
function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing && /^[a-zA-Z0-9_-]{12,100}$/.test(existing)) return existing;
    const id = crypto.randomUUID().replace(/-/g, '');
    sessionStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch { return crypto.randomUUID().replace(/-/g, ''); }
}
function deviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent;
  if (/ipad|tablet/i.test(ua)) return 'tablet';
  return /mobi|android|iphone/i.test(ua) ? 'mobile' : 'desktop';
}
function isPreview(): boolean {
  return location.pathname.startsWith('/cms-preview') || location.hostname.startsWith('preview.') || window.self !== window.top;
}
export function trackEvent(action: 'page_view' | 'form_submit' | 'email_subscribe' | 'button_click', path = location.pathname, item?: string): void {
  if (isPreview() || navigator.doNotTrack === '1' || !/^\/(?!\/)/.test(path)) return;
  const visitor = visitorIdentity();
  if (!visitor) return;
  const domain = location.hostname.toLowerCase().replace(/^www\./, '');
  if (!domain || domain === 'localhost') return;
  const referrer = (() => { try { const url = new URL(document.referrer); return url.origin === location.origin ? '' : url.origin; } catch { return ''; } })();
  void apiClient.post('/web/statistics/events', {
    action, item, ...visitor, sessionId: getSessionId(), path: path.slice(0, 500), referrer: referrer.slice(0, 500), device: deviceType(),
  }, { headers: { 'X-Website-Domain': domain }, timeout: 5000 }).catch(() => { /* Analytics must never interrupt visitors. */ });
}
/** Capture interactions without reading input values or arbitrary user-provided text. */
export function trackButtonClick(target: Element): void {
  if (isPreview()) return;
  const element = target.closest('button, a, [role="button"]');
  if (!element || element.closest('[data-no-analytics], [contenteditable="true"]')) return;
  if (element instanceof HTMLButtonElement && element.disabled) return;
  const label = element.getAttribute('data-analytics-id') ||
    element.getAttribute('aria-label') ||
    (element instanceof HTMLAnchorElement ? 'link' : 'button');
  // Explicit identifiers only: never send button text or input values.
  const safeLabel = label.trim().replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 80);
  if (!safeLabel) return;
  trackEvent('button_click', location.pathname, safeLabel);
}

export function trackPageView(path: string): void {
  if (lastPageView === path) return;
  lastPageView = path;
  trackEvent('page_view', path);
}
